import React, { useState } from 'react';
import Modal from '../Modal';
import { useAuth } from '../../helpers/AuthContext';
import './ProductBulkEditor.css';

export default function ProductBulkDelete({
    selectedProducts,
    allProducts,
    onClose,
    onSave,
    onError,
}) {
    const { fetch } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const selectedProductsData = allProducts.filter(product =>
        selectedProducts.includes(product.uuid)
    );

    const handleClose = () => {
        if (!isSubmitting) onClose();
    };

    const handleDelete = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            const response = await fetch('/account/products/bulk-delete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ productIds: selectedProducts }),
            });
            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(result.error || 'Failed to delete products');
            }

            const deletedNoun = result.deletedCount === 1 ? 'product' : 'products';
            const deletedMessage = result.deletedCount > 0
                ? `Successfully deleted ${result.deletedCount} ${deletedNoun}.`
                : '';
            const blockedNames = (result.blocked || []).map(product => product.name).join(', ');
            const blockedSubject = result.blockedCount === 1
                ? 'product was'
                : 'products were';
            const blockedMessage = result.blockedCount > 0
                ? `${result.blockedCount} ${blockedSubject} not deleted because of active or `
                    + `unfulfilled orders${blockedNames ? `: ${blockedNames}` : ''}.`
                : '';

            onSave(deletedMessage, blockedMessage);
            onClose();
        } catch (error) {
            console.error('Error deleting products:', error);
            onError(error.message || 'Failed to delete products. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const modalFooter = (
        <div className="bulk-editor-actions">
            <button
                type="button"
                className="button secondary"
                onClick={handleClose}
                disabled={isSubmitting}
            >
                Cancel
            </button>
            <button
                type="button"
                className="button danger"
                onClick={handleDelete}
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Deleting...' : 'Delete Products'}
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={true}
            onClose={handleClose}
            title={
                `Delete ${selectedProducts.length} `
                + `${selectedProducts.length === 1 ? 'Product' : 'Products'}`
            }
            size="medium"
            footer={modalFooter}
            closeOnOverlayClick={!isSubmitting}
            closeOnEscape={!isSubmitting}
            ariaDescribedBy="bulk-delete-content"
        >
            <div className="bulk-editor-content" id="bulk-delete-content">
                <p className="error">
                    <strong>
                        This permanently deletes the selected products and cannot be undone.
                    </strong>
                </p>
                <p>Products with active or unfulfilled orders will be kept.</p>
                <div className="selected-products">
                    <h4>Selected Products:</h4>
                    <ul>
                        {selectedProductsData.map(product => (
                            <li key={product.uuid}>
                                {product.name} ({product.type})
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </Modal>
    );
}
